import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Truck, MapPin, Shield, ArrowRight, MessageCircle, Phone, HeadphonesIcon, PackageCheck } from 'lucide-react';

// Assets
const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_cf0d5347-2064-4d56-91a1-8465a8c8b38b/artifacts/xbsk2yuj_image.png';
const DELIVERY_IMAGE = 'https://customer-assets.emergentagent.com/job_brand-first-delivery/artifacts/ikkn4zu3_image.png';

const valueProps = [
  {
    icon: Truck,
    title: 'Customer Experience Delivery',
    description: 'Professional delivery with live tracking, ETA notifications and responsive customer support.',
    color: 'bg-red-100 text-drop24-red',
  },
  {
    icon: MapPin,
    title: 'Live Tracking',
    description: 'Real-time tracking updates for you and your customers at every stage.',
    color: 'bg-blue-100 text-blue-600',
  },
  {
    icon: PackageCheck,
    title: 'Premium Handling',
    description: 'Specialized care for small parcels, fragile items, and premium products.',
    color: 'bg-amber-100 text-amber-600',
  },
];

// GTM tracking
const trackEvent = (eventName, eventData = {}) => {
  if (window.dataLayer) {
    window.dataLayer.push({ event: eventName, ...eventData });
  }
};

export function HomePage() {
  return (
    <div data-testid="home-page" className="overflow-hidden">
      {/* Hero Section - MLM Style */}
      <section className="min-h-screen pt-24 pb-16 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center min-h-[80vh]">
            {/* Left - Text Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <h1 
                data-testid="hero-title"
                className="font-serif text-5xl md:text-6xl lg:text-7xl text-gray-900 leading-tight mb-6"
              >
                We Deliver Your <span className="text-gradient">Brand</span> The Way You Would.
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Customer experience delivery solutions for South African online stores.
                We focus on the customer experience after checkout through live tracking, proactive communication and professional last-mile execution.
              </p>
              
              {/* CTA Buttons - MLM Style */}
              <div className="flex flex-wrap gap-4 mb-8">
                <Link
                  to="/contact"
                  data-testid="hero-primary-cta"
                  className="inline-flex items-center gap-2 bg-drop24-red text-white hover:bg-[#c4191f] px-8 py-4 rounded-full font-semibold transition-all shadow-soft hover:shadow-soft-lg"
                >
                  Get a Quote
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <a
                  href="https://wa.me/27660719068"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('contact_click', { contact_type: 'whatsapp', location: 'hero' })}
                  data-testid="hero-whatsapp-cta"
                  className="inline-flex items-center gap-2 bg-white text-gray-700 hover:bg-gray-50 px-8 py-4 rounded-full font-semibold border border-gray-200 transition-all"
                >
                  <MessageCircle className="w-5 h-5 text-green-500" />
                  Chat on WhatsApp
                </a>
              </div>
              
              {/* Contact Link */}
              <a
                href="tel:+27660719068"
                onClick={() => trackEvent('contact_click', { contact_type: 'phone', location: 'hero' })}
                className="inline-flex items-center gap-2 text-gray-500 hover:text-drop24-red transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span>066 071 9068</span>
              </a>
            </motion.div>

            {/* Right - Image with Floating Card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="order-1 lg:order-2 relative"
            >
              <div className="relative">
                <img
                  src={HERO_IMAGE}
                  alt="Logistics Infrastructure"
                  className="w-full rounded-3xl shadow-soft-xl"
                />
                {/* Floating Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-soft-xl p-4 flex items-center gap-3"
                >
                  <div className="w-12 h-12 bg-drop24-yellow rounded-xl flex items-center justify-center">
                    <Shield className="w-6 h-6 text-yellow-800" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Ethical Logistics</p>
                    <p className="text-sm text-gray-500">Every rider matters</p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why This Matters Section - MLM Style */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src={DELIVERY_IMAGE}
                alt="Delivery Rider"
                className="w-full rounded-3xl shadow-soft-lg"
              />
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-drop24-red font-semibold text-sm uppercase tracking-wider">Why This Matters</span>
              <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mt-4 mb-6">
                Your Brand Experience Doesn't End at Checkout
              </h2>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                Every delivery is a brand touchpoint. When your rider hands over a parcel, they're 
                representing your brand, not ours. That's why we invest in training, equipment, 
                and culture.
              </p>
              
              <ul className="space-y-4 mb-8">
                {[
                  'Professional delivery representatives',
                  'Brand-conscious parcel presentation',
                  'Real-time tracking & communication',
                  'Specialized fragile item handling'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-drop24-red-light flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-drop24-red" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              
              <Link
                to="/delivery"
                className="inline-flex items-center gap-2 text-drop24-red font-semibold hover:gap-3 transition-all"
              >
                Learn more about delivery
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Value Props - MLM Card Style */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-drop24-red font-semibold text-sm uppercase tracking-wider">What We Offer</span>
            <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mt-4">
              Logistics That Puts Your <span className="text-gradient">Brand First</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {valueProps.map((prop, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`feature-card-${index}`}
                className="bg-white rounded-3xl p-8 shadow-soft card-shadow-hover text-center"
              >
                <div className={`w-16 h-16 ${prop.color} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                  <prop.icon className="w-8 h-8" />
                </div>
                <h3 className="font-semibold text-xl text-gray-900 mb-3">{prop.title}</h3>
                <p className="text-gray-600">{prop.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Split - MLM Style */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-drop24-red font-semibold text-sm uppercase tracking-wider">Our Services</span>
            <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mt-4">
              Two Pillars of <span className="text-gradient">Success</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Delivery Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl border border-gray-200 p-8 md:p-10 shadow-soft card-shadow-hover"
            >
              <div className="w-14 h-14 bg-drop24-red-light rounded-2xl flex items-center justify-center mb-6">
                <Truck className="w-7 h-7 text-drop24-red" />
              </div>
              <h3 className="font-serif text-2xl md:text-3xl text-gray-900 mb-4">Last-Mile Delivery</h3>
              <p className="text-gray-600 mb-6">
                Customer experience delivery that represents your brand from dispatch to doorstep. 
                Fast, trackable, and professionally handled.
              </p>
              <ul className="space-y-3 mb-8">
                {['Same-day & next-day options', 'Live tracking for all parties', 'Fragile item specialization', 'Professional delivery handling'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-gray-600">
                    <div className="w-1.5 h-1.5 rounded-full bg-drop24-red" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/delivery"
                data-testid="service-delivery-cta"
                className="inline-flex items-center gap-2 bg-gray-900 text-white hover:bg-gray-800 px-6 py-3 rounded-full font-semibold transition-all"
              >
                Learn About Delivery
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            {/* Customer Service Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-gray-900 rounded-3xl p-8 md:p-10 text-white shadow-soft-xl"
            >
              <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center mb-6">
                <HeadphonesIcon className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-serif text-2xl md:text-3xl mb-4">Customer Service</h3>
              <p className="text-gray-300 mb-6">
                We don't just deliver parcels — we deliver peace of mind. 
                Real people, real solutions, real care.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Live Tracking & ETA Notifications',
                  'Proactive Customer Communication',
                  'Dedicated Support — 3-5 Min Response',
                  'Problem Solvers — We Go The Extra Mile',
                  'Estate Access & Address Assistance',
                  'Returns & Exchanges Support',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-gray-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-drop24-red" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                data-testid="service-customer-service-cta"
                className="inline-flex items-center gap-2 bg-drop24-red text-white hover:bg-[#c4191f] px-6 py-3 rounded-full font-semibold transition-all"
              >
                Get In Touch
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-6">
              Ready to Deliver <span className="text-gradient">Better</span>?
            </h2>
            <p className="text-xl text-gray-600 mb-10">
              Whether you need reliable delivery — we're here to help your 
              ecommerce brand succeed.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                data-testid="cta-primary-button"
                className="inline-flex items-center gap-2 bg-drop24-red text-white hover:bg-[#c4191f] px-8 py-4 rounded-full font-semibold transition-all shadow-soft hover:shadow-soft-lg"
              >
                Get a Quote
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="https://wa.me/27660719068"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('contact_click', { contact_type: 'whatsapp', location: 'cta' })}
                className="inline-flex items-center gap-2 bg-white text-gray-700 hover:bg-gray-50 px-8 py-4 rounded-full font-semibold border border-gray-200 transition-all"
              >
                <MessageCircle className="w-5 h-5 text-green-500" />
                Chat on WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/27660719068?text=Hi%2C%20I'm%20interested%20in%20Drop24%20logistics%20services"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackEvent('contact_click', { contact_type: 'whatsapp', location: 'floating_button' })}
        className="whatsapp-float bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
        data-testid="whatsapp-float-button"
      >
        <MessageCircle className="w-6 h-6" />
        <span className="hidden md:inline font-medium">Chat with us</span>
      </a>
    </div>
  );
}

export default HomePage;
